import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { AGENTS_PATH } from '../../config';
import { tokens } from '../../theme';
import type { AgentVersionListProps } from './types';

/**
 * Every released version of an agent, newest first, as links — the way a package
 * page lists its versions. The newest is marked **Latest**, and the one on screen
 * is marked as the current page.
 *
 * **The version is in the query string, not the path**, so `/toolkit/agents/x` is
 * always the newest and a link to a particular one is `?version=1.0.0`. A reader
 * pasting an address gets what the address says, and the bare address never goes
 * stale.
 *
 * The first entry is the newest because the service orders them; this does not
 * sort. Sorting here would be a second ordering rule beside the one that knows
 * `1.10.0` follows `1.9.0`.
 */
export const AgentVersionList = ({ agentName, versions, selected }: AgentVersionListProps) => (
  <Box
    component="ul"
    sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 1 }}
  >
    {versions.map((version, index) => {
      const isSelected = version === selected;

      return (
        <Box component="li" key={version}>
          <RouterLink
            to={`${AGENTS_PATH}/${encodeURIComponent(agentName)}?version=${encodeURIComponent(version)}`}
            aria-current={isSelected ? 'page' : undefined}
            style={{ textDecoration: 'none' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                paddingInline: 3,
                paddingBlock: 2,
                borderRadius: `${String(tokens.radius.md)}px`,
                backgroundColor: isSelected ? 'surface.sunken' : 'transparent',
                '&:hover': { backgroundColor: 'surface.sunken' },
              }}
            >
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontFamily: tokens.fontFamily.mono,
                  color: isSelected ? 'text.primary' : 'text.link',
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                {version}
              </Typography>

              {index === 0 && (
                <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
                  Latest
                </Typography>
              )}
            </Box>
          </RouterLink>
        </Box>
      );
    })}
  </Box>
);
